'use client'

import * as React from 'react'
import { cn } from '../../utils/cn'
import { VanMooseLogo } from '../common/VanMooseLogo'

interface FooterColumn {
  title: string
  links: {
    label: string
    href: string
  }[]
}

interface SocialLink {
  name: string
  href: string
  icon: React.ReactNode
}

interface FooterProps {
  logo: React.ReactNode
  columns: FooterColumn[]
  bottomLinks: {
    label: string
    href: string
  }[]
  socialLinks?: SocialLink[]
}

export function Footer({
  logo,
  columns,
  bottomLinks,
  socialLinks
}: FooterProps) {
  const currentYear = new Date().getFullYear()

  return (
    <footer className="bg-[var(--landing-bg-muted)] border-t border-[var(--landing-border)]">
      <div className="landing-container py-16">
        {/* Main Footer Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8 mb-12">
          {/* Logo Column */}
          <div className="col-span-2">
            <div className="mb-4">{logo}</div>
            {socialLinks && socialLinks.length > 0 && (
              <div className="flex items-center gap-4 mt-4">
                {socialLinks.map((link) => (
                  <a
                    key={link.name}
                    href={link.href}
                    className={cn(
                      'text-[var(--landing-text-muted)] hover:text-[var(--landing-text-primary)]',
                      'transition-colors duration-150'
                    )}
                    aria-label={link.name}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {link.icon}
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Link Columns */}
          {columns.map((column) => (
            <div key={column.title}>
              <h3 className="text-sm font-semibold text-[var(--landing-text-primary)] mb-4">
                {column.title}
              </h3>
              <ul className="space-y-3">
                {column.links.map((link) => {
                  const isExternal = link.href.startsWith('http')
                  return (
                    <li key={link.href}>
                      <a
                        href={link.href}
                        {...(isExternal && { target: '_blank', rel: 'noopener noreferrer' })}
                        className={cn(
                          'text-sm text-[var(--landing-text-muted)]',
                          'hover:text-[var(--landing-text-primary)]',
                          'transition-colors duration-150'
                        )}
                      >
                        {link.label}
                      </a>
                    </li>
                  )
                })}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-[var(--landing-border)]">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-4">
              <p className="text-sm text-[var(--landing-text-muted)]">
                © {currentYear} Van Moose. All rights reserved.
              </p>
              <span className="hidden md:block text-[var(--landing-border-strong)]">·</span>
              <a
                href="https://vanmoose.com"
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  'inline-flex items-center gap-1.5 whitespace-nowrap',
                  'text-sm text-[var(--landing-text-muted)]',
                  'hover:text-[var(--landing-text-primary)]',
                  'transition-colors duration-150'
                )}
              >
                <VanMooseLogo size={16} />
                <span>A Van Moose Product</span>
              </a>
            </div>
            <div className="flex items-center gap-6">
              {bottomLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className={cn(
                    'text-sm text-[var(--landing-text-muted)]',
                    'hover:text-[var(--landing-text-primary)]',
                    'transition-colors duration-150'
                  )}
                >
                  {link.label}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}
